import React, { memo } from "react";
import { createPortal } from "react-dom";
import AddLinkCard from "./AddLinkCard";
import type { UrlData } from "../services/firebase";

interface EditLinkModalProps {
  open: boolean;
  editData: UrlData | null;
  loading?: boolean;
  onSave: (link: { url: string; name?: string; tags?: string[]; col?: number }) => void;
  onClose: () => void;
}

const EditLinkModal: React.FC<EditLinkModalProps> = ({
  open,
  editData,
  loading = false,
  onSave,
  onClose,
}) => {
  if (!open || !editData) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 animate-[fadeIn_150ms_ease-out]"
        onClick={onClose}
      />

      {/* Modal panel — full width on mobile, centered card on desktop */}
      <div
        className="relative z-10 w-full sm:max-w-md bg-[var(--color-bg-secondary)]
                   border border-[var(--color-border)]
                   rounded-t-xl sm:rounded-[var(--radius-lg)]
                   shadow-lg animate-[fadeIn_150ms_ease-out]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)]">
          <h3 className="text-base font-semibold font-heading text-[var(--color-text-primary)]">
            Edit Link
          </h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-[var(--color-bg-hover)] transition-colors
                       focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
            aria-label="Close"
          >
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5 text-[var(--color-text-tertiary)]"
            >
              <path
                fillRule="evenodd"
                d="M10 8.586l4.95-4.95a1 1 0 111.414 1.414L11.414 10l4.95 4.95a1 1 0 01-1.414 1.414L10 11.414l-4.95 4.95a1 1 0 01-1.414-1.414l4.95-4.95-4.95-4.95A1 1 0 015.05 3.636l4.95 4.95z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {/* Keyed by link so pointing the modal at another link starts a fresh form. */}
          <AddLinkCard
            key={editData.id}
            onAdd={(link) => {
              onSave(link);
              onClose();
            }}
            loading={loading}
            editData={editData}
            onCancelEdit={onClose}
            onDismiss={onClose}
            hideCancel={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default memo(EditLinkModal);
